import { ThemeProps, styled } from '@teko/ds-desktop';
import { getTrackingData } from '@teko/tracking';
import { hasComboDiscount } from '@teko/teko-ecommerce';
import { ProductCardType, ProductListConfig } from '@teko/types/ecommerce';
import { useTenantConfig } from 'contexts';
import { markUserIsOver18 } from 'helpers';
import { t } from 'i18n';
import {
  EHamperCardType,
  HamperCard,
} from 'modules/page-builder/slots/HamperCarousel/HamperCard';
import { ConfirmOver18YearOldModal } from 'modules/product-detail/shared/ConfirmOver18YearOldModal';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

type HamperGridProps = ProductListConfig & {
  hamperItemKey: string;
};

export const HamperGrid: React.FC<HamperGridProps> = ({
  products,
  productsPerRow = 5,
  regionName,
  type = 'grid',
  itemsPerPage,
  className,
  hamperItemKey,
}: HamperGridProps) => {
  const router = useRouter();
  const { isMobile } = useTenantConfig();
  const [showPopupCheckAge, setShowPopupCheckAge] = useState(false);

  const page = router.query.page ? Number(router.query.page) : 1;

  const handleSetShowPopupCheckAge = (value: boolean) => {
    setShowPopupCheckAge(value);
    if (!value) {
      markUserIsOver18();
    }
  };

  return (
    <Wrapper isMobile={isMobile} className={className}>
      {products?.map((item, index) => (
        <Grid
          isMobile={isMobile}
          key={index}
          productsPerRow={productsPerRow}
          type={type}
        >
          <HamperCard
            {...item}
            hamperCardType={EHamperCardType.HAMPER_ITEM}
            hamperItemKey={hamperItemKey}
            tracking={
              regionName
                ? getTrackingData({
                    isTrackContent: true,
                    regionName,
                    contentName: item.sku,
                    target: 'productDetail',
                    index: itemsPerPage
                      ? index + (page - 1) * itemsPerPage
                      : index,
                    payload: hasComboDiscount(item.bestComboDiscount)
                      ? JSON.stringify({ promotionType: 'combo' })
                      : undefined,
                  })
                : {}
            }
            type={type}
            setShowPopupCheckAge={setShowPopupCheckAge}
          />
        </Grid>
      ))}

      <ConfirmOver18YearOldModal
        showPopupCheckAge={showPopupCheckAge}
        setShowPopupCheckAge={handleSetShowPopupCheckAge}
        okText={t('Tôi trên 18 tuổi')}
        onCancel={() => setShowPopupCheckAge(false)}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ isMobile }: ThemeProps & { isMobile: boolean }) => `
    display: flex;
    flex-wrap: wrap;
    column-gap: 2px;
    place-content: flex-start space-between;
    padding: 2px 0;
    &::after{
      content: "";
      flex: auto;
    }
    ${isMobile ? 'padding: 5px;' : ''}
  `,
);

interface GridProps {
  productsPerRow: number;
  type: ProductCardType;
}

const Grid = styled.div(
  ({ productsPerRow, type, isMobile }: GridProps & { isMobile: boolean }) => `
  background: white;
  margin-bottom: 2px;
  ${
    type === 'grid'
      ? `width: calc(${100 / productsPerRow}% - 2px);`
      : 'width: 100%;'
  }
  ${isMobile ? 'border-radius: 8px;' : ''}
  }`,
);
